import { memo, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { IClientOption } from '~/store/client/types';
import MenuList from '~/ui/components/inputs/SelectWithoutAnimation/components/MenuList';
import ClientMultiSelect from '~/ui/pages/Clients/reusable/ClientMultiSelect';
import useAllocationChangeTracking from '../../../shared/hooks/useAllocationChangeTracking';
import { ClientWithAllocation, IEditAllocationFormValues } from '../../../shared/model/types';
import useSelectAdditionalStyles from '../../hooks/useSelectAdditionalStyles';
import { normalizeClientOptionsByTeamToAllocations } from '../../utils';

interface IProps {
  loading: boolean;
  loadMore: VoidFunction;
  teamMemberIdx: number;
  updateAllocations: (selectedClients: ClientWithAllocation[]) => void;
}

const AllocationClientsSelectGU = ({
  loading,
  loadMore,
  teamMemberIdx,
  updateAllocations,
}: IProps): ReactElement => {
  const { watch } = useFormContext<IEditAllocationFormValues>();

  const { additionalStyleHandler } = useSelectAdditionalStyles(teamMemberIdx);

  const teamMembersAllocations = watch('teamMembersAllocations');

  const teamMemberAllocations = useMemo(
    () => teamMembersAllocations[teamMemberIdx],
    [teamMembersAllocations, teamMemberIdx],
  );

  const clientsAllocationsFields = useMemo(
    () => teamMemberAllocations.clientsAllocations,
    [teamMemberAllocations.clientsAllocations],
  );

  const { markChanged, markDeleted, unmarkDeleted } =
    useAllocationChangeTracking<IEditAllocationFormValues>({
      changedField: 'changedAllocationsIds',
      deletedField: 'deletedAllocationsIds',
    });

  const groupedExistingClientsByClientId = useMemo(
    () => new Map(clientsAllocationsFields.map(field => [field.clientId, field])),
    [clientsAllocationsFields],
  );

  const trackAllocationChange = useCallback(
    (existedAllocationId: number | null | undefined, hasOptions: boolean) => {
      if (!existedAllocationId) return;
      if (hasOptions) {
        unmarkDeleted(existedAllocationId);
        markChanged(existedAllocationId);
        return;
      }

      markDeleted(existedAllocationId);
    },
    [markChanged, markDeleted, unmarkDeleted],
  );

  const handleSelectClientOptionsByTeam = useCallback(
    (options: IClientOption[]) => {
      const existedAllocationId =
        clientsAllocationsFields.find(alloc => alloc.allocation?.id)?.allocation.id ?? null;

      trackAllocationChange(existedAllocationId, options.length > 0);
      const hasExistingOption = clientsAllocationsFields.some(field =>
        options.some(option => option.id === field.id),
      );
      let selectedOptions: Array<IClientOption | ClientWithAllocation> = [];

      if (options.length) {
        selectedOptions = hasExistingOption ? options : [...clientsAllocationsFields, ...options];
      }

      updateAllocations(
        normalizeClientOptionsByTeamToAllocations(selectedOptions, existedAllocationId).map(
          client => groupedExistingClientsByClientId.get(client.clientId) ?? client,
        ),
      );
    },
    [
      clientsAllocationsFields,
      groupedExistingClientsByClientId,
      trackAllocationChange,
      updateAllocations,
    ],
  );

  const handleUpdateCurrentClientOption = useCallback(
    (options: IClientOption[]) => {
      handleSelectClientOptionsByTeam(options);
    },
    [handleSelectClientOptionsByTeam],
  );

  return (
    <ClientMultiSelect
      loading={loading}
      loadMore={loadMore}
      label="Select Clients to Assign"
      customComponents={{ MenuList }}
      additionalStyleHandler={additionalStyleHandler}
      onSelect={handleUpdateCurrentClientOption}
    />
  );
};

export default memo(AllocationClientsSelectGU);
